import { useEffect, useRef, useCallback } from 'react';
import { createJobStreamTicket } from '../services/complianceService';

const SSE_RECONNECT_BASE_MS = 1000;
const SSE_RECONNECT_MAX_MS = 60000;

function getStudentServiceBase() {
  return (process.env.REACT_APP_ATHENIAN_STUDENT_SERVICE_URL || 'http://127.0.0.1:5001/api/students').replace(/\/$/, '');
}

function buildJobStreamUrl(studentId, jobId, ticket) {
  const base = getStudentServiceBase();
  const path = jobId
    ? `/${studentId}/jobs/${encodeURIComponent(jobId)}/stream`
    : `/${studentId}/jobs/stream`;
  return `${base}${path}?ticket=${encodeURIComponent(ticket)}`;
}

function parseEventData(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return { raw };
  }
}

/**
 * Subscribe to student job SSE stream with reconnect backoff.
 * Uses short-lived stream tickets (no JWT in URL).
 */
export function useJobStream({ studentId, jobId = null, enabled, onEvent, onConnectionChange }) {
  const onEventRef = useRef(onEvent);
  const onConnectionRef = useRef(onConnectionChange);
  onEventRef.current = onEvent;
  onConnectionRef.current = onConnectionChange;

  const handlePayload = useCallback((payload) => {
    if (!payload || payload.event === 'heartbeat') return;
    onEventRef.current?.(payload);
  }, []);

  useEffect(() => {
    if (!enabled || !studentId) return undefined;

    let es = null;
    let reconnectTimer = null;
    let cancelled = false;
    let reconnectDelay = SSE_RECONNECT_BASE_MS;

    const setConnected = (value) => {
      onConnectionRef.current?.(value);
    };

    const connect = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.hidden) {
        reconnectTimer = setTimeout(connect, reconnectDelay);
        return;
      }

      try {
        const { data } = await createJobStreamTicket(studentId);
        const ticket = data?.data?.ticket;
        if (!ticket) {
          throw new Error('Stream ticket missing');
        }
        es = new EventSource(buildJobStreamUrl(studentId, jobId, ticket));
      } catch {
        setConnected(false);
        reconnectTimer = setTimeout(connect, reconnectDelay);
        reconnectDelay = Math.min(reconnectDelay * 2, SSE_RECONNECT_MAX_MS);
        return;
      }

      es.onopen = () => {
        reconnectDelay = SSE_RECONNECT_BASE_MS;
        setConnected(true);
      };

      const forward = (e) => handlePayload(parseEventData(e.data));

      es.onmessage = forward;
      ['job_queued', 'job_queue_position', 'job_started', 'job_progress', 'job_completed', 'job_failed', 'pipeline_complete', 'checklist_sync_completed', 'stream_reconnected', 'stream_reconnecting', 'stream_error', 'heartbeat'].forEach((name) => {
        es.addEventListener(name, forward);
      });

      es.onerror = () => {
        setConnected(false);
        es.close();
        es = null;
        if (!cancelled) {
          reconnectTimer = setTimeout(connect, reconnectDelay);
          reconnectDelay = Math.min(reconnectDelay * 2, SSE_RECONNECT_MAX_MS);
        }
      };
    };

    const onVisible = () => {
      if (!cancelled && (!es || es.readyState === EventSource.CLOSED)) {
        clearTimeout(reconnectTimer);
        connect();
      }
    };

    connect();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      clearTimeout(reconnectTimer);
      if (es) {
        es.close();
      }
      setConnected(false);
    };
  }, [studentId, jobId, enabled, handlePayload]);
}
